import { ICellRendererParams } from "@ag-grid-community/core";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import React, { useCallback, useMemo } from "react";
import { FundFamilyUseAllocationTableContext } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import { createFundFamilyUseAllocationTableEmptyRow } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableUtils";
import { MaybernTableAddRemoveButtons } from "shared/components/table/maybern/MaybernTableAddRemoveButtons";
import invariant from "shared/utils/TinyInvariant";

export const FundFamilyUseAllocationTableAddRemoveCellRenderer: React.FC<
  ICellRendererParams<FundFamilyUseAllocationTableRow>
> = (params) => {
  const {
    tableDef,
    onRowAdd,
    onRowRemove,
  }: FundFamilyUseAllocationTableContext = params.context;

  const { categories } = tableDef;
  const { category_id }: FundFamilyUseAllocationTableRow = params.data!;

  const category = useMemo(
    () => categories.find((c) => c.id === category_id)!,
    [category_id, categories],
  );
  invariant(category, `Category ${category_id} not found`);

  // this is needed to update the Add/Remove button visibility
  const queueRefreshCells = useCallback(() => {
    setTimeout(() => {
      params.api.refreshCells({
        force: true,
      });
    });
  }, [params.api]);

  const onClickAdd = useCallback(() => {
    onRowAdd?.(createFundFamilyUseAllocationTableEmptyRow(tableDef, category));
    queueRefreshCells();
  }, [onRowAdd, tableDef, category, queueRefreshCells]);

  const onClickRemove = useCallback(() => {
    onRowRemove?.(params.node);
    queueRefreshCells();
  }, [onRowRemove, params.node, queueRefreshCells]);

  const parentNodes = params.node.parent?.childrenAfterSort;

  const lastNode = parentNodes?.[parentNodes.length - 1];
  const rowCount = parentNodes?.length ?? 0;

  const showAddButton = lastNode?.id === params.node.id;
  const isReadonlyRow = params.data?.is_read_only || false;
  const showRemoveButton = rowCount > 1 && !isReadonlyRow;

  return (
    <MaybernTableAddRemoveButtons
      params={params}
      showAddButton={showAddButton}
      showRemoveButton={showRemoveButton}
      onClickAdd={onClickAdd}
      onClickRemove={onClickRemove}
    />
  );
};
